import type { Prediction } from '../types';

const LOCAL_KEY = 'musr-predictions';

export function getOwnerKey(): string {
  const existing = localStorage.getItem('musr-owner-key');
  if (existing) return existing;
  const created = crypto.randomUUID();
  localStorage.setItem('musr-owner-key', created);
  return created;
}

function getLocalPredictions(): Prediction[] {
  try {
    return JSON.parse(localStorage.getItem(LOCAL_KEY) || '[]') as Prediction[];
  } catch {
    return [];
  }
}

function saveLocalPredictions(items: Prediction[]) {
  localStorage.setItem(LOCAL_KEY, JSON.stringify(items));
}

export async function loadPredictions(ownerKey: string): Promise<{ items: Prediction[]; mode: 'cloud' | 'local' }> {
  try {
    const response = await fetch(`/api/bootstrap?owner=${encodeURIComponent(ownerKey)}`);
    if (!response.ok) throw new Error('Cloud API unavailable');
    const data = await response.json() as { predictions: Prediction[] };
    saveLocalPredictions(data.predictions);
    return { items: data.predictions, mode: 'cloud' };
  } catch {
    return { items: getLocalPredictions(), mode: 'local' };
  }
}

export async function createPrediction(prediction: Prediction): Promise<{ item: Prediction; mode: 'cloud' | 'local' }> {
  try {
    const response = await fetch('/api/predictions', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(prediction),
    });
    if (!response.ok) throw new Error('Cloud save failed');
    const data = await response.json() as { prediction: Prediction };
    const next = [data.prediction, ...getLocalPredictions().filter(item => item.id !== data.prediction.id)];
    saveLocalPredictions(next);
    return { item: data.prediction, mode: 'cloud' };
  } catch {
    const next = [prediction, ...getLocalPredictions().filter(item => item.id !== prediction.id)];
    saveLocalPredictions(next);
    return { item: prediction, mode: 'local' };
  }
}

export async function scorePrediction(id: string, ownerKey: string, actual: Partial<Prediction>): Promise<{ item: Prediction; mode: 'cloud' | 'local' }> {
  try {
    const response = await fetch(`/api/predictions/${encodeURIComponent(id)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ownerKey, ...actual }),
    });
    if (!response.ok) throw new Error('Cloud scoring failed');
    const data = await response.json() as { prediction: Prediction };
    saveLocalPredictions(getLocalPredictions().map(item => item.id === id ? data.prediction : item));
    return { item: data.prediction, mode: 'cloud' };
  } catch {
    const items = getLocalPredictions();
    const current = items.find(item => item.id === id);
    if (!current) throw new Error('Prediction not found');
    const actualXI = actual.actualStartingXI || [];
    const lineup = current.startingXI.filter(player => actualXI.includes(player)).length * 5;
    const formation = current.formation === actual.actualFormation ? 5 : 0;
    const firstScorer = current.firstScorer === actual.actualFirstScorer ? 20 : 0;
    const motm = current.motm === actual.actualMotm ? 20 : 0;
    const finalScore = current.homeScore === actual.actualHomeScore && current.awayScore === actual.actualAwayScore ? 20 : 0;
    const updated: Prediction = {
      ...current,
      ...actual,
      status: 'scored',
      scoreBreakdown: { lineup, formation, firstScorer, motm, finalScore },
      scoreTotal: lineup + formation + firstScorer + motm + finalScore,
      updatedAt: new Date().toISOString(),
    };
    saveLocalPredictions(items.map(item => item.id === id ? updated : item));
    return { item: updated, mode: 'local' };
  }
}
